import React, { useEffect, useState } from 'react';
import CountUp from 'react-countup';
import { cn } from '../../lib/utils';

/**
 * MoneyCounter Component
 * 
 * Animated money counter using react-countup with monospace font for tabular alignment.
 * Supports scroll-driven value updates and customizable formatting.
 * 
 * @param {Object} props
 * @param {number} props.end - Target value to count up to
 * @param {number} props.start - Starting value (default: 0)
 * @param {number} props.duration - Animation duration in seconds (default: 1.4)
 * @param {string} props.prefix - Text before number (default: '$')
 * @param {string} props.suffix - Text after number
 * @param {string} props.separator - Thousands separator (default: ',')
 * @param {number} props.decimals - Decimal places (default: 0)
 * @param {string} props.className - Additional Tailwind classes
 * @param {string} props.testId - data-testid attribute
 * @returns {JSX.Element}
 */
export const MoneyCounter = ({
  end = 50000,
  start = 0,
  duration = 1.4,
  prefix = '$',
  suffix = '',
  separator = ',',
  decimals = 0,
  className = '',
  testId = 'money-counter',
  style = {},
}) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    // Slight delay to ensure component is mounted before animating
    const timer = setTimeout(() => setShouldAnimate(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <span
      className={cn(
        // Monospace font for tabular numbers
        'font-mono',
        'tabular-nums',
        // Size and color (can be overridden)
        'text-3xl sm:text-4xl lg:text-5xl',
        'text-teal-300',
        // Smooth transitions
        'transition-colors duration-300',
        className
      )}
      style={style}
      data-testid={testId}
    >
      {shouldAnimate ? (
        <CountUp
          start={start}
          end={end}
          duration={duration}
          separator={separator}
          decimals={decimals}
          prefix={prefix}
          suffix={suffix}
          useEasing={true}
          easingFn={(t, b, c, d) => {
            // easeOutExpo easing for smooth deceleration
            return c * (-Math.pow(2, -10 * t / d) + 1) + b;
          }}
        />
      ) : (
        `${prefix}${start.toLocaleString()}${suffix}`
      )}
    </span>
  );
};